import * as processHandler from '../processHandler';
import * as testimCloudflare from './testimCloudflare';
import ora from 'ora';
import { getLogger } from './logger';
import { gridTypes } from './constants';
import { LambdatestService } from '../services/lambdatestService';
import type { RunnerOptions, TunnelOptions } from 'src/runOptions';

const logger = getLogger('tunnel');
type Options = Partial<TunnelOptions> | RunnerOptions;

const shouldUseLambdatestTunnel = (options: Options) => 
    [gridTypes.TESTIM_LAMBDATEST, gridTypes.HYBRID].includes(options.gridData?.type as any) &&
    options.gridData!.tunnel === 'lambdatest' &&
    // Make sure we have a user and key for the lambdatest tunnel
    (options.gridData!.tunnelUser && options.gridData!.tunnelUser.length > 0) &&
    (options.gridData!.tunnelKey && options.gridData!.tunnelKey.length > 0);

const logTunnelProgress = (message: 'connecting' | 'connected' | 'disconnecting' | 'disconnected', options: Options, actualTunnel: string) => logger.info(
    `${actualTunnel} tunnel, status: ${message}`, {
    companyId: options.company?.companyId,
    projectId: options.projectData?.projectId,
    testId: (options as RunnerOptions).testId,
    // The tunnel on the grid object, if the user uses ngrok he might get another tunnel type (depends on the useNgrokTunnel FF)
    desiredTunnel: options.gridData?.tunnel,
    actualTunnel,
});

export const connect = async (options: Options) => {
    if (!options.tunnel) {
        return;
    }

    let spinner;
    let tunnelType: 'lambdatest' | 'cloudflare' | null = null;
    try {
        spinner = ora('Starting tunnel...').start();
        if (shouldUseLambdatestTunnel(options)) {
            tunnelType = 'lambdatest';
            logTunnelProgress('connecting', options, tunnelType);
            await LambdatestService.connectTunnel(options);
            logTunnelProgress('connected', options, tunnelType);
        } else {
            tunnelType = 'cloudflare';
            logTunnelProgress('connecting', options, tunnelType);
            await testimCloudflare.connectTunnel(options);
            logTunnelProgress('connected', options, tunnelType);
        }
        spinner.succeed();
    } catch (err) {
        let msg = 'Failed to start tunnel. Please contact support@testim.io';
        if (err.isInvalidCredsForLT) msg = 'Could not connect to LambdaTest. Check your credentials and then try again.';
        logger.error('Failed to open tunnel', { err });
        spinner?.fail(msg);
        throw new Error(err.isInvalidCredsForLT ? `${msg}\n${err.stdoutResult}` : msg);
    }
};

export const disconnect = async (options: Options) => {
    if (!options.tunnel) {
        return;
    }

    let tunnelType: 'lambdatest' | 'cloudflare' | null = null;
    try {
        if (shouldUseLambdatestTunnel(options)) {
            tunnelType = 'lambdatest';
            logTunnelProgress('disconnecting', options, tunnelType);
            await LambdatestService.disconnectTunnel(options);
            logTunnelProgress('disconnected', options, tunnelType);
        } else {
            tunnelType = 'cloudflare';
            logTunnelProgress('disconnecting', options, tunnelType);
            await testimCloudflare.disconnectTunnel(options);
            logTunnelProgress('disconnected', options, tunnelType);
        }
    } catch (err) {
        const msg = 'catch error - failed to close tunnel';
        logger.error(msg, { err });
        throw new Error(msg);
    }
};

export const serveTunneling = async (options: Partial<TunnelOptions>, waitFor = new Promise(() => { /* avoid exiting process */ })) => {
    await connect(options);
    processHandler.registerExitHook(() => disconnect(options));
    return await waitFor;
};
